import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionId, getToken, getGuestExamUser } from "../utils/storage";
import { ProgressBar } from "@/components/ProgressBar";

const Task1ProcessingScreen = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  // IDENTICAL message philosophy as CombinedFeedbackProcessing
  const statusMessages = [
    "Reviewing your Task 1 response...",
    "Measuring your grammar and vocabulary...",
    "Preparing your personalized feedback...",
  ];

  // -----------------------------
  // Audio cleanup (unchanged)
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        const globalAudioRef = (window as any).audioServiceRef?.current;
        if (globalAudioRef) {
          globalAudioRef.stopRecording?.();
          globalAudioRef.cancelAIAudio?.();
          globalAudioRef.cleanup?.();
        }
      } catch {}

      try {
        if (navigator?.mediaDevices?.getUserMedia) {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          try {
            s.getTracks().forEach((t) => {
              try {
                (MediaStreamTrack.prototype as any).stop.call(t);
              } catch {
                try {
                  t.stop();
                } catch {}
              }
            });
          } catch {}
        }
      } catch {}

      try {
        const ctx: AudioContext | undefined = (window as any).__appAudioContext;
        if (ctx?.close) {
          await ctx.close();
          (window as any).__appAudioContext = undefined;
        }
      } catch {}
    })();
  }, []);

  // -----------------------------
  // Status message cycling
  // -----------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [statusMessages.length]);

  // -----------------------------
  // Task 1 Retry Processing Logic (unchanged)
  // -----------------------------
  useEffect(() => {
    const processTask1Retry = async () => {
      try {
        const sessionIdFromStorage = getSessionId();
        const currentExamId = localStorage.getItem("current_exam_id");
        const sessionId = currentExamId || sessionIdFromStorage;

        const paidToken = getToken();
        const guestUser = getGuestExamUser();
        const guestToken =
          guestUser?.token ?? localStorage.getItem("guestToken");
        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;

        if (!token) {
          setIsProcessing(false);
          setIsComplete(true);
          return;
        }
        // Call grammar analysis for Task 1 retry
        try {
          console.log('🎯 TASK 1 RETRY PROCESSING - Calling grammar analysis...');
          
          const grammarPayload: any = { 
            session_id: sessionId,
            task_type: 1,
            task_attempt_id: localStorage.getItem('current_task_attempt_id')
          };
          if (isGuest) {
            grammarPayload.guest_id = guestUser?.user_id;
            grammarPayload.is_guest = true;
          }
          
          const grammarResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/task-1-2-individual', {
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(grammarPayload),
          });

          if (grammarResponse.ok) {
            const result = await grammarResponse.json();
            if (result.analysis_data) {
              localStorage.setItem(
                "latest_analysis_data",
                JSON.stringify(result.analysis_data)
              );
            }
          }
        } catch (innerError) {
          console.error("Task 1 grammar analysis error:", innerError);
        }

        await new Promise((res) => setTimeout(res, 1500));
        setIsProcessing(false);
        setIsComplete(true);
      } catch (error) {
        console.error("Task 1 retry error:", error);
        setIsProcessing(false);
        setIsComplete(true);
      }
    };

    processTask1Retry();
  }, []);

  // -----------------------------
  // Navigate after progress bar completes
  // -----------------------------
  useEffect(() => {
    if (!isComplete) return;

    const timeout = setTimeout(() => {
      navigate("/single-task-feedback");
    }, 400);

    return () => clearTimeout(timeout);
  }, [isComplete, navigate]);

  // -----------------------------
  // FINAL UI — PERFECT MATCH TO CombinedFeedbackProcessing
  // -----------------------------
  return (
    <div
      className="
        flex flex-col items-center justify-center
        relative overflow-hidden
        h-full
        px-4 py-12
        md:px-6 md:py-16
      "
    >
      <div className="flex flex-col items-center text-center max-w-2xl mx-auto">

        {/* Spinner Icon — identical dimensions */}
        <div className="relative mb-6">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>

        {/* Title — identical style */}
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Preparing Your Task 1 Results
        </h1>

        {/* Status Message — spacing identical */}
        <div className="space-y-3 mb-6">
          <p className="text-lg md:text-xl text-muted-foreground">
            {statusMessages[currentMessageIndex]}
          </p>
        </div>

        {/* Progress Bar — same component */}
        <ProgressBar isComplete={isComplete} />

        {/* Bottom explanatory text — identical wording & style */}
        <div className="mt-8 text-sm text-muted-foreground max-w-xl mx-auto">
          <p>
            This usually takes less than a minute. Please keep this tab open so your
            results and next-step tips can load.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Task1ProcessingScreen;
